import { type SubmitEvent as ReactSubmitEvent, type FormEvent, memo, useState } from "react";
import "Components/ConfigLayout/Event/AlarmEventSettings/AlarmEventSettings.less";
import { Card, Input, type SelectOption, Select, SettingsLayout, Switch } from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectAlarmEvent, setAlarmEventAction } from "Data/Redux/Slices/Settings/Event/Alarm";
import { isEqual } from "lodash";
import { produce } from "immer";
import { eventPopoverContent, Operation } from "Data/Objects/Event";
import ActionButtons from "Components/ConfigLayout/ActionButtons";

const operationOptions: SelectOption[] = [
	{
		label: Operation.NORMALLY_OPEN,
		value: Operation.NORMALLY_OPEN
	},
	{
		label: Operation.NORMALLY_CLOSED,
		value: Operation.NORMALLY_CLOSED
	}
];

type AlarmEventForm = {
	alarmName: HTMLInputElement;
	alarmOut: HTMLInputElement;
	snapshot: HTMLInputElement;
	whiteLight: HTMLInputElement;
	redBlueLight: HTMLInputElement;
	audioAlarm: HTMLInputElement;
	recording: HTMLInputElement;
	email: HTMLInputElement;
	ftp: HTMLInputElement;
} & HTMLFormElement;

function AlarmEventSettings() {
	const dispatch = useAppDispatch();
	const alarmEventSettings = useAppSelector(selectAlarmEvent);
	const [ localAlarmEventSettings, setLocalAlarmEventSettings ] = useState(alarmEventSettings);
	const {
		name, operation, alarmOut, snapshot, whiteLight, redBlueLight, audioAlarm, recording, email, ftp
	} = localAlarmEventSettings;

	const setAlarmEvent = () => dispatch(setAlarmEventAction(localAlarmEventSettings));
	const disabled = isEqual(localAlarmEventSettings, alarmEventSettings);

	const handleAlarmOutOnChange = (newEnabled: boolean) => {
		setLocalAlarmEventSettings(produce(localAlarmEventSettings, draft => {
			draft.alarmOut = newEnabled;
		}));
	};

	const handleSnapshotOnChange = (newEnabled: boolean) => {
		setLocalAlarmEventSettings(produce(localAlarmEventSettings, draft => {
			draft.snapshot = newEnabled;
		}));
	};

	const handleWhiteLightOnChange = (newEnabled: boolean) => {
		setLocalAlarmEventSettings(produce(localAlarmEventSettings, draft => {
			draft.whiteLight = newEnabled;
		}));
	};

	const handleRedBlueLightOnChange = (newEnabled: boolean) => {
		setLocalAlarmEventSettings(produce(localAlarmEventSettings, draft => {
			draft.redBlueLight = newEnabled;
		}));
	};

	const handleAudioAlarmOnChange = (newEnabled: boolean) => {
		setLocalAlarmEventSettings(produce(localAlarmEventSettings, draft => {
			draft.audioAlarm = newEnabled;
		}));
	};

	const handleRecordingOnChange = (newEnabled: boolean) => {
		setLocalAlarmEventSettings(produce(localAlarmEventSettings, draft => {
			draft.recording = newEnabled;
		}));
	};

	const handleEmailOnChange = (newEnabled: boolean) => {
		setLocalAlarmEventSettings(produce(localAlarmEventSettings, draft => {
			draft.email = newEnabled;
		}));
	};

	const handleFtpChange = (newEnabled: boolean) => {
		setLocalAlarmEventSettings(produce(localAlarmEventSettings, draft => {
			draft.ftp = newEnabled;
		}));
	};

	const handleFormReset = (event: FormEvent<AlarmEventForm>) => {
		event.preventDefault();

		setLocalAlarmEventSettings(alarmEventSettings);
	};

	const handleFormSubmit = (event: ReactSubmitEvent<AlarmEventForm>) => {
		event.preventDefault();

		if (event.currentTarget?.checkValidity()) {
			setAlarmEvent();
		}
	};

	const handleOperationSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalAlarmEventSettings(produce(localAlarmEventSettings, draft => {
			draft.operation = value as Operation;
		}));
	};

	return (
		<SettingsLayout.Form
			className="alarm-settings"
			noValidate
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="medium" title="Alarm Event Settings" popoverContent={ eventPopoverContent }>
					<div className="alarm-settings-content">
						<div className="form-row">
							<span>Name</span>
							<Input wrapClassName="form-input" name="alarmName" value={ name } />
						</div>
						<div className="form-row">
							<span>Operation</span>
							<Select
								allowClear={ false }
								className="form-select"
								onSelect={ handleOperationSelect }
								options={ operationOptions }
								value={ operation }
							/>
						</div>
						<div className="form-row">
							<span>Alarm Out</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleAlarmOutOnChange } checked={ alarmOut } />
							</div>
						</div>
						<div className="form-row">
							<span>Snapshot</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleSnapshotOnChange } checked={ snapshot } />
							</div>
						</div>
						<div className="form-row">
							<span>White Light</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleWhiteLightOnChange }checked={ whiteLight } />
							</div>
						</div>
						<div className="form-row">
							<span>Red & Blue Light</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleRedBlueLightOnChange } checked={ redBlueLight } />
							</div>
						</div>
						<div className="form-row">
							<span>Audio Alarm</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleAudioAlarmOnChange } checked={ audioAlarm } />
							</div>
						</div>
						<div className="form-row">
							<span>Recording</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleRecordingOnChange } checked={ recording } />
							</div>
						</div>
						<div className="form-row">
							<span>E-Mail</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleEmailOnChange } checked={ email } />
							</div>
						</div>
						<div className="form-row">
							<span>FTP</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleFtpChange } checked={ ftp } />
							</div>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(AlarmEventSettings);
